"use client";

import { clsx } from "clsx";
import { kebabCase } from "lodash";
import Link from "next/link";
import { useState } from "react";
import {
  FacetGroup as FacetGroupType,
  FacetGroupProduct,
} from "@repo/apis/single-product";
import { AspectRatio } from "@repo/ui/aspect-ratio";
import { Carousel } from "@repo/ui/carousel";
import { CloudinaryImage } from "@repo/ui/cloudinary-image";
import * as Flex from "@repo/ui/flex";
import * as Grid from "@repo/ui/grid";
import * as SheetComponent from "@repo/ui/sheet";
import { useStore } from "@repo/ui/store";
import { prependUrlWithHttps } from "@repo/utils/prepend-url-with-https";
import * as Viewport from "@/components/viewport";
import { CAROUSEL } from "@/constants";
import { AddIcon } from "@/theme/icons/add";
import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";
import { CloseIcon } from "@/theme/icons/close";
import {
  TYPOGRAPHY_HEADING4,
  TYPOGRAPHY_LABEL3_BOLD,
  TYPOGRAPHY_LABEL3,
} from "@/theme/topshop/typography/typography-constants";
import classes from "./facet-group.module.css";

export const Facet = ({
  facet,
  onMouseEnter,
  onMouseLeave,
}: {
  facet: FacetGroupProduct;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  const { countryData } = useStore();
  const buildFacetUrl = () =>
    `${countryData.siteBasePath}/product/${kebabCase(facet.name)}/${facet.productId}`;

  return (
    <Link
      href={buildFacetUrl()}
      className={clsx(
        classes.imageWrapper,
        facet.isSelected && classes.selected,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <AspectRatio ratio={0.78}>
        {!facet.isInStock && (
          <div className={classes.outOfStock}>
            <div className={classes.outOfStockBefore} />
          </div>
        )}
        <CloudinaryImage
          alt={facet.name}
          url={prependUrlWithHttps(facet.imageUrl)}
          srcSize={168}
          sourceSetProfile={CAROUSEL}
        />
      </AspectRatio>
    </Link>
  );
};

export const FacetSheet = ({ facetGroup }: { facetGroup: FacetGroupType }) => {
  const facets = facetGroup.facets[0]?.products ?? [];

  if (!facets.length) return null;

  const getSelectedFacet = () => {
    return facets.find((facet) => facet.isSelected);
  };

  const initialFacets = (() => {
    const selectedIndex = facets.findIndex((facet) => facet.isSelected);
    if (selectedIndex === -1) return facets.slice(0, 2);
    return [facets[selectedIndex], facets[selectedIndex + 1] || facets[0]];
  })() as FacetGroupProduct[];

  return (
    <SheetComponent.Root>
      <SheetComponent.Trigger aria-label="View more colours">
        <Flex.Container gap="2xs" className={classes.trigger}>
          {initialFacets.map((facet: FacetGroupProduct) => (
            <Flex.Item
              key={facet.productId}
              grow={2}
              className={clsx(facet.isSelected && classes.selected)}
            >
              <div className={classes.imageWrapper}>
                {!facet.isInStock && (
                  <div className={classes.outOfStock}>
                    <div className={classes.outOfStockBefore} />
                  </div>
                )}
                <CloudinaryImage
                  alt={facet.name}
                  url={prependUrlWithHttps(facet.imageUrl)}
                  srcSize={168}
                  sourceSetProfile={CAROUSEL}
                  className={classes.facetImageTrigger}
                />
              </div>
            </Flex.Item>
          ))}
          <Flex.Item grow={1} align="center">
            <AddIcon />
          </Flex.Item>
        </Flex.Container>
      </SheetComponent.Trigger>
      <SheetComponent.Content
        position="bottom"
        overlayAnimation
        className={classes.sheet}
      >
        <SheetComponent.Header>
          <Flex.Container justify="space-between" className={classes.header}>
            <SheetComponent.Title className={TYPOGRAPHY_HEADING4}>
              COLOUR
            </SheetComponent.Title>
            <span className={TYPOGRAPHY_LABEL3_BOLD}>
              {getSelectedFacet()?.description}
            </span>
          </Flex.Container>
          <SheetComponent.Close className={classes.closeBtn}>
            <CloseIcon className={classes.closeIcon} />
          </SheetComponent.Close>
        </SheetComponent.Header>
        <SheetComponent.Main className={classes.main}>
          <Grid.Container columns={5} columnGap="2xs" rowGap="sm">
            {facets.map((facet) => (
              <Grid.Item
                key={facet.productId}
                className={clsx(
                  facet.isSelected && classes.selected,
                  classes.item,
                )}
              >
                <Facet facet={facet} />
              </Grid.Item>
            ))}
          </Grid.Container>
        </SheetComponent.Main>
      </SheetComponent.Content>
    </SheetComponent.Root>
  );
};

export const FacetCarousel = ({
  facetGroup,
}: {
  facetGroup: FacetGroupType;
}) => {
  const facets = facetGroup.facets[0]?.products ?? [];
  const [hoveredDescription, setHoveredDescription] = useState<string | null>(
    null,
  );

  if (!facets?.length) return null;

  const selectedFacet = facets.find((facet) => facet.isSelected);
  const currentDescription =
    hoveredDescription ?? selectedFacet?.description ?? "";

  return (
    <Flex.Container column gap="xs">
      <Carousel
        itemsToShow={5}
        className={classes.carousel}
        gap={4}
        nextIcon={<ChevronRight />}
        prevIcon={<ChevronLeft />}
        prevAriaLabel="Previous item"
        nextAriaLabel="Next item"
      >
        {facets.map((facet) => (
          <div
            className={clsx(facet.isSelected && classes.selected)}
            key={facet.productId}
          >
            <Facet
              facet={facet}
              key={facet.productId}
              onMouseEnter={() => setHoveredDescription(facet.description)}
              onMouseLeave={() => setHoveredDescription(null)}
            />
          </div>
        ))}
      </Carousel>
      <span className={TYPOGRAPHY_LABEL3}>{currentDescription}</span>
    </Flex.Container>
  );
};

export const FacetGroup = ({ facetGroup }: { facetGroup: FacetGroupType }) => {
  if (!facetGroup?.facets?.length) return null;

  return (
    <>
      <Viewport.Small>
        <FacetSheet facetGroup={facetGroup} />
      </Viewport.Small>
      <Viewport.Large>
        <FacetCarousel facetGroup={facetGroup} />
      </Viewport.Large>
    </>
  );
};
